import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError } from '@/lib/api';
import {
  getUsers,
  getUser,
  getUserReadModel,
  updateUser,
  searchOrganisationsPaginated,
  getOrganisationByMongoId,
  searchAccounts,
  getAccountByOrganisationId,
  createUser,
  checkEmailUnique,
  getNewUserOptions,
  updateOrganisationProfile,
  getUserSessions,
  getUserSecurityLog,
  removeUserFromOrganisation,
  deleteUser,
  getRoles,
  updateUserSecurityState,
} from '../api';
import { userKeys } from './queryKeys';
import type {
  UsersFilters,
  UsersResponse,
  UpdateUserInput,
  CreateUserInput,
  UpdateOrganisationProfileInput,
  User,
  UpdateUserSecurityStateInput,
} from '../types';

/**
 * Helper to extract error message from unknown error
 */
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'An unexpected error occurred';
}

function invalidateUserQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  userId?: string,
) {
  queryClient.invalidateQueries({ queryKey: userKeys.lists() });
  queryClient.invalidateQueries({ queryKey: ['account-users'] });
  queryClient.invalidateQueries({ queryKey: ['account-users-search'] });

  if (userId) {
    queryClient.invalidateQueries({ queryKey: userKeys.detail(userId) });
  }
}

function mergeUserSecurityState(current: User | undefined, patch: User): User | undefined {
  if (!current) return current;
  if (current._id !== patch._id) return current;
  return {
    ...current,
    enabled: patch.enabled,
    force_password_reset: patch.force_password_reset,
    incorrect_login_attempts: patch.incorrect_login_attempts,
    last_login_attempt: patch.last_login_attempt,
  };
}

function updateCachedUserSecurityState(
  queryClient: ReturnType<typeof useQueryClient>,
  userId: string,
  patch: User,
) {
  queryClient.setQueryData<User | undefined>(
    userKeys.detail(userId),
    (current) => mergeUserSecurityState(current, patch),
  );

  queryClient.setQueriesData<UsersResponse>(
    { queryKey: userKeys.lists() },
    (current) => {
      if (!current?.users) return current;
      return {
        ...current,
        users: current.users.map((user) => mergeUserSecurityState(user, patch) || user),
      };
    },
  );
}

const READ_MODEL_SYNC_TIMEOUT_MS = 15000;
const READ_MODEL_POLL_INTERVAL_MS = 500;

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getOrganisationProfile(user: User, organisationId: string) {
  return user.organisations?.find((profile) => {
    const organisation = profile.organisation;
    if (typeof organisation === 'string') return organisation === organisationId;
    return organisation?._id === organisationId;
  });
}

function sorted(values: string[] | undefined) {
  return [...(values || [])].sort();
}

function sameStringArrays(left: string[] | undefined, right: string[] | undefined) {
  return JSON.stringify(sorted(left)) === JSON.stringify(sorted(right));
}

function sameJsonValue(left: unknown, right: unknown) {
  return JSON.stringify(left ?? null) === JSON.stringify(right ?? null);
}

async function waitForReadModel(
  predicate: () => Promise<boolean>,
  errorMessage: string,
) {
  const startedAt = Date.now();
  let lastError: unknown;

  while (Date.now() - startedAt < READ_MODEL_SYNC_TIMEOUT_MS) {
    try {
      if (await predicate()) return;
    } catch (error) {
      lastError = error;
    }

    await sleep(READ_MODEL_POLL_INTERVAL_MS);
  }

  if (lastError instanceof Error) throw lastError;
  throw new Error(errorMessage);
}

async function waitForUserReadModel(
  userId: string,
  predicate: (user: User) => boolean,
  errorMessage: string,
) {
  await waitForReadModel(async () => {
    const user = await getUserReadModel(userId);
    return predicate(user);
  }, errorMessage);
}

async function waitForUserReadModelDeletion(userId: string) {
  await waitForReadModel(async () => {
    try {
      await getUserReadModel(userId);
      return false;
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        return true;
      }
      throw error;
    }
  }, 'Timed out waiting for user deletion to reach the PostgreSQL read model');
}

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Hook to fetch users with filters
 */
export function useUsers(filters: UsersFilters = {}) {
  return useQuery({
    queryKey: userKeys.list(filters),
    queryFn: () => getUsers(filters),
  });
}

/**
 * Hook to fetch a single user by ID
 */
export function useUser(userId: string | undefined) {
  return useQuery({
    queryKey: userKeys.detail(userId!),
    queryFn: () => getUser(userId!),
    enabled: !!userId,
  });
}

const ORG_PAGE_SIZE = 50;

/**
 * Paginated organisations search with infinite scrolling.
 * Used by the Accounts filter combobox.
 */
export function useOrganisationsInfinite(search: string) {
  return useInfiniteQuery({
    queryKey: userKeys.organisationsInfinite(search),
    queryFn: ({ pageParam = 0 }) =>
      searchOrganisationsPaginated({
        search,
        pageNumber: pageParam,
        pageLength: ORG_PAGE_SIZE,
      }),
    getNextPageParam: (lastPage) => {
      const loaded = (lastPage.pageNumber + 1) * lastPage.pageLength;
      return loaded < lastPage.total ? lastPage.pageNumber + 1 : undefined;
    },
    initialPageParam: 0,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Look up a single organisation by mongoId.
 * Used to resolve the selected-org label when it's not in any loaded page
 * (e.g. after page reload with a pre-selected org).
 */
export function useOrganisation(mongoId: string | undefined) {
  return useQuery({
    queryKey: userKeys.organisation(mongoId ?? ''),
    queryFn: () => getOrganisationByMongoId(mongoId!),
    enabled: !!mongoId && mongoId !== 'all',
    staleTime: 5 * 60 * 1000,
  });
}

export function useAccountSearch(search: string, enabled = true) {
  return useQuery({
    queryKey: userKeys.accountSearch(search),
    queryFn: () => searchAccounts(search),
    enabled,
    staleTime: 30 * 1000,
  });
}

export function useAccountByOrganisationId(organisationId: string, enabled = true) {
  return useQuery({
    queryKey: userKeys.accountByOrganisationId(organisationId),
    queryFn: () => getAccountByOrganisationId(organisationId),
    enabled: enabled && organisationId.trim().length > 0,
    staleTime: 5 * 60 * 1000,
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Hook to update a user
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, data }: { userId: string; data: UpdateUserInput }) => {
      const user = await updateUser(userId, data);
      await waitForUserReadModel(
        userId,
        (readUser) =>
          (data.username === undefined || readUser.username === data.username) &&
          (data.email === undefined || readUser.email === data.email) &&
          (data.firstname === undefined || readUser.firstname === data.firstname) &&
          (data.lastname === undefined || readUser.lastname === data.lastname) &&
          (data.phone === undefined || readUser.phone === data.phone) &&
          (data.extension === undefined || readUser.extension === data.extension) &&
          (data.mobile === undefined || readUser.mobile === data.mobile) &&
          (data.company === undefined || readUser.company === data.company) &&
          (data.position === undefined || readUser.position === data.position) &&
          (data.language === undefined || readUser.language === data.language) &&
          (data.enabled === undefined || readUser.enabled === data.enabled) &&
          (data.is_subaccount === undefined || readUser.is_subaccount === data.is_subaccount) &&
          (data.force_password_reset === undefined
            || readUser.force_password_reset === data.force_password_reset) &&
          (data.login_ttl_hrs === undefined || readUser.login_ttl_hrs === data.login_ttl_hrs) &&
          (data.contact_methods === undefined
            || sameJsonValue(readUser.contact_methods, data.contact_methods)),
        'Timed out waiting for user updates to reach the PostgreSQL read model',
      );
      return user;
    },
    onSuccess: (_user, variables) => {
      invalidateUserQueries(queryClient, variables.userId);
      toast.success('User updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update user', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to create a new user
 */
export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateUserInput) => {
      const user = await createUser(data);
      // V4 PG-only: `id` (PG UUID) is the canonical primary key returned by
      // POST /v4/users. Fall back to `pg_id` for older response shapes.
      await waitForUserReadModel(
        (user.id || user.pg_id) as string,
        (readUser) => {
          const profile = getOrganisationProfile(readUser, data.organisation);
          return (
            readUser.username === data.username &&
            readUser.email === data.email &&
            (data.enabled === undefined || readUser.enabled === data.enabled) &&
            (data.is_subaccount === undefined || readUser.is_subaccount === data.is_subaccount) &&
            (data.force_password_reset === undefined
              || readUser.force_password_reset === data.force_password_reset) &&
            (data.login_ttl_hrs === undefined || readUser.login_ttl_hrs === data.login_ttl_hrs) &&
            (data.extension === undefined || readUser.extension === data.extension) &&
            (data.contact_methods === undefined
              || sameJsonValue(readUser.contact_methods, data.contact_methods)) &&
            !!profile &&
            (data.admin === undefined || profile.admin_level === data.admin) &&
            sameStringArrays(
              profile.usergroups?.map((usergroup) =>
                typeof usergroup === 'string' ? usergroup : usergroup._id,
              ),
              data.usergroups,
            ) &&
            sameStringArrays(
              profile.sensorgroups?.map((sensorgroup) =>
                typeof sensorgroup === 'string' ? sensorgroup : sensorgroup._id,
              ),
              data.sensorgroups,
            ) &&
            sameStringArrays(profile.tags, data.tags) &&
            (data.description === undefined || (profile.description || '') === data.description)
          );
        },
        'Timed out waiting for the new user to appear in the PostgreSQL read model',
      );
      return user;
    },
    onSuccess: () => {
      invalidateUserQueries(queryClient);
      toast.success('User created successfully');
    },
    onError: (error) => {
      toast.error('Failed to create user', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to check email uniqueness
 */
export function useCheckEmailUnique() {
  return useMutation({
    mutationFn: (email: string) => checkEmailUnique(email),
  });
}

/**
 * Hook to fetch usergroup/sensorgroup options for an organisation
 */
export function useNewUserOptions(
  organisationId: string | undefined,
  accountId?: string,
) {
  return useQuery({
    queryKey: [...userKeys.newUserOptions(organisationId!), accountId || 'legacy'],
    queryFn: () => getNewUserOptions(organisationId!, accountId),
    enabled: !!organisationId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}

/**
 * Hook to update a user's organisation profile
 */
export function useUpdateOrganisationProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      organisationId,
      data,
    }: {
      userId: string;
      organisationId: string;
      data: UpdateOrganisationProfileInput;
    }) => updateOrganisationProfile(userId, organisationId, data).then(async () => {
      await waitForUserReadModel(
        userId,
        (readUser) => {
          const profile = getOrganisationProfile(readUser, organisationId);
          if (!profile) return false;

          return (
            (data.admin === undefined || profile.admin_level === data.admin) &&
            sameStringArrays(
              profile.usergroups?.map((usergroup) =>
                typeof usergroup === 'string' ? usergroup : usergroup._id,
              ),
              data.usergroups,
            ) &&
            sameStringArrays(
              profile.sensorgroups?.map((sensorgroup) =>
                typeof sensorgroup === 'string' ? sensorgroup : sensorgroup._id,
              ),
              data.sensorgroups,
            ) &&
            sameStringArrays(profile.tags, data.tags) &&
            (data.description === undefined || (profile.description || '') === data.description)
          );
        },
        'Timed out waiting for organisation profile changes to reach the PostgreSQL read model',
      );
    }),
    onSuccess: (_result, variables) => {
      invalidateUserQueries(queryClient, variables.userId);
      toast.success('Organisation profile updated');
    },
    onError: (error) => {
      toast.error('Failed to update organisation profile', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to fetch session log for a user
 */
export function useUserSessions(userId: string | undefined) {
  return useQuery({
    queryKey: userKeys.sessions(userId!),
    queryFn: () => getUserSessions(userId!),
    enabled: !!userId,
  });
}

export function useUserSecurityLog(
  userId: string | undefined,
  options: { refetchInterval?: number | false } = {},
) {
  return useQuery({
    queryKey: userKeys.securityLog(userId!),
    queryFn: () => getUserSecurityLog(userId!),
    enabled: !!userId,
    refetchInterval: options.refetchInterval,
  });
}

/**
 * Hook to fetch roles
 */
export function useRoles() {
  return useQuery({
    queryKey: userKeys.roles(),
    queryFn: getRoles,
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
  });
}

/**
 * Hook to remove a user from an organisation
 */
export function useRemoveUserFromOrganisation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, organisationId }: { userId: string; organisationId: string }) => {
      const user = await removeUserFromOrganisation(userId, organisationId);
      await waitForUserReadModel(
        userId,
        (readUser) => !getOrganisationProfile(readUser, organisationId),
        'Timed out waiting for organisation removal to reach the PostgreSQL read model',
      );
      return user;
    },
    onSuccess: (_user, variables) => {
      invalidateUserQueries(queryClient, variables.userId);
      toast.success('User removed from organisation');
    },
    onError: (error) => {
      toast.error('Failed to remove user from organisation', { description: getErrorMessage(error) });
    },
  });
}

/**
 * Hook to delete a user entirely
 */
export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const result = await deleteUser(userId);
      await waitForUserReadModelDeletion(userId);
      return result;
    },
    onSuccess: (_user, userId) => {
      invalidateUserQueries(queryClient, userId);
      toast.success('User deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete user', { description: getErrorMessage(error) });
    },
  });
}

export function useUpdateUserSecurityState() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, data }: { userId: string; data: UpdateUserSecurityStateInput }) => {
      // `userId` is the MONGO id (the PATCH route is /v4/users/:mongoUserId/security-state),
      // but the PG read-model lookup (getUserReadModel → assertPgId) only accepts a PG UUID.
      // Use pg_id from the PATCH response for the read-model wait — passing the Mongo id
      // made assertPgId throw, so the UI reported failure on a write that had succeeded.
      const user = await updateUserSecurityState(userId, data);
      const pgId = user.pg_id ?? user.id;
      if (pgId) {
        await waitForUserReadModel(
          pgId,
          (readUser) =>
            (data.enabled === undefined || readUser.enabled === data.enabled) &&
            (data.force_password_reset === undefined
              || readUser.force_password_reset === data.force_password_reset),
          'Timed out waiting for security state changes to reach the PostgreSQL read model',
        );
      }
      return user;
    },
    onSuccess: (user, variables) => {
      // The detail cache (useUser) is keyed by PG UUID, not the Mongo id the
      // mutation was called with — patch it under the right key or the modal's
      // toggles keep rendering the pre-mutation state.
      updateCachedUserSecurityState(queryClient, user.pg_id ?? user.id ?? variables.userId, user);
      queryClient.invalidateQueries({ queryKey: userKeys.securityLog(variables.userId) });
      toast.success('Security settings updated');
    },
    onError: (error) => {
      toast.error('Failed to update security settings', { description: getErrorMessage(error) });
    },
  });
}
